import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { ArrowLeft, Info, RefreshCw, ShieldAlert } from 'lucide-react'
import { useMemo, useState } from 'react'

import type { VerificationOverride } from '@/api/types'
import { useBranchSelection } from '@/components/app-shell/use-branch-selection'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { PageHeader } from '@/components/page-header'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { CUSTOMER_PERMISSIONS, useCustomerPermissions } from '@/features/customers/permissions'
import { useVerificationOverrides } from '@/features/customers/queries'
import { TellerLink } from '@/features/teller/teller-link'
import { branchLabel } from '@/lib/branch'
import { formatDateTime } from '@/lib/datetime'

/**
 * Supervisor oversight for the one verification method that needs neither a device nor a specimen.
 *
 * `Override` exists precisely because the ordinary methods sometimes cannot be used — no scanner,
 * no photograph on file, an injury, anything that needs a person to decide anyway — and it is
 * deliberately unblocking: it costs a teller nothing extra and stops nobody at the counter. What
 * makes that safe rather than a hole in the control is this screen: every override a teller records
 * lands here, named, timed, and with the reason they gave, for somebody who was not standing at the
 * counter to read afterwards.
 *
 * This is oversight after the fact, not a gate before it — there is no approve or reject here, only
 * a record. `GET /customers/verifications/overrides` returns every one within the caller's reach;
 * the branch filter below narrows it, and clearing it goes back to everything the caller may act for.
 */

const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined
const DEFAULT_PAGE_SIZE = 25

const helper = dataTableColumns<VerificationOverride>()

export function VerificationOverridesPage() {
  const permissions = useCustomerPermissions()
  const { branches } = useBranchSelection()
  const [branchId, setBranchId] = useState<string | undefined>(undefined)
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  })

  const query = useVerificationOverrides(branchId)
  const rows = useMemo(() => query.data ?? [], [query.data])
  const pageRows = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize
    return rows.slice(start, start + pagination.pageSize)
  }, [rows, pagination])

  const columns = useMemo<DataTableColumn<VerificationOverride>[]>(
    () =>
      helper.columns([
        helper.accessor('customerFullName', {
          header: 'Customer',
          enableSorting: false,
          cell: (context) => (
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-medium text-heading">
                {context.row.original.customerFullName ?? 'Unknown'}
              </span>
              <span className="truncate font-mono text-2xs text-muted">
                {context.row.original.customerId}
              </span>
            </span>
          ),
          meta: { card: 'title' },
        }),
        helper.accessor('branchId', {
          header: 'Branch',
          enableSorting: false,
          cell: (context) => (
            <span className="text-sm text-body">
              {branchLabel(branches.find((branch) => branch.id === context.row.original.branchId))}
            </span>
          ),
          meta: { widthClass: 'w-32', card: 'secondary' },
        }),
        helper.accessor('verifiedBy', {
          header: 'Teller',
          enableSorting: false,
          cell: (context) => (
            <span className="text-sm text-body">{context.row.original.verifiedBy ?? '—'}</span>
          ),
          meta: { widthClass: 'w-36', card: 'secondary' },
        }),
        helper.accessor('overrideReason', {
          header: 'Reason',
          enableSorting: false,
          cell: (context) => (
            <span className="block max-w-md text-sm text-body">
              {context.row.original.overrideReason}
            </span>
          ),
          meta: { card: 'secondary' },
        }),
        helper.accessor('verifiedAtUtc', {
          header: 'Recorded',
          enableSorting: false,
          cell: (context) => (
            <span className="numeric text-sm text-body tabular-nums">
              {formatDateTime(context.row.original.verifiedAtUtc)}
            </span>
          ),
          meta: { widthClass: 'w-44', card: 'lead' },
        }),
      ]),
    [branches],
  )

  if (!permissions.canReviewVerificationOverrides) {
    return (
      <>
        <TellerLink
          to="/teller"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover"
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          Back to the counter
        </TellerLink>
        <PageHeader title="Verification overrides" />
        <Alert tone="warning">
          <ShieldAlert aria-hidden />
          <div className="space-y-1">
            <AlertTitle>This screen needs {CUSTOMER_PERMISSIONS.verificationReview}</AlertTitle>
            <AlertDescription>
              Your role does not hold it, so the review queue is not read. Ask an administrator if
              you are meant to review overrides.
            </AlertDescription>
          </div>
        </Alert>
      </>
    )
  }

  return (
    <>
      <TellerLink
        to="/teller"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        Back to the counter
      </TellerLink>

      <PageHeader
        title="Verification overrides"
        description="Every counter verification recorded without a photo, signature, thumbprint, or fingerprint comparison — reviewed after the fact, never blocked before it."
        actions={
          <Button variant="secondary" onClick={() => void query.refetch()} disabled={query.isFetching}>
            <RefreshCw />
            {query.isFetching ? 'Refreshing…' : 'Refresh'}
          </Button>
        }
      />

      <Alert tone="info">
        <Info aria-hidden />
        <AlertDescription>
          This is a record, not a queue to clear — there is nothing to approve or reject here.
          Follow up with the teller named on a row the same way any other unusual activity is
          followed up.
        </AlertDescription>
      </Alert>

      {branches.length > 1 ? (
        <div className="max-w-xs space-y-1.5">
          <Select
            value={branchId ?? 'all'}
            onValueChange={(value) => {
              setBranchId(value === 'all' ? undefined : value)
              setPagination((current) => ({ ...current, pageIndex: 0 }))
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="All branches within reach" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All branches within reach</SelectItem>
              {branches.map((branch) => (
                <SelectItem key={branch.id} value={branch.id}>
                  {branchLabel(branch)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        data={query.isPending ? undefined : pageRows}
        rowCount={rows.length}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={UNSORTED}
        onSortingChange={ignoreSortingChange}
        isLoading={query.isPending}
        isFetching={query.isFetching}
        error={query.isError ? query.error : null}
        onRetry={() => void query.refetch()}
        getRowId={(row) => row.id}
        pageSizeOptions={[DEFAULT_PAGE_SIZE, 50, 100, 200]}
        emptyTitle="No overrides recorded"
        emptyDescription="Every verification within reach used a photo, signature, thumbprint, or fingerprint comparison. Nothing here needs following up."
      />
    </>
  )
}
